import { useCallback, useState } from 'react';

import type { UnsplashImage } from '@/components/asset-manager/types';
import { cn } from '@/lib/utils';
import { ImageMenu } from './image-menu';
import { LoadingOverlay, PhotoCredit } from './image-overlays';

interface ImageCardProps {
  image: UnsplashImage;
  mode?: 'menu' | 'select';
  onAddToSiteFiles?: (image: UnsplashImage) => void;
  onCrop?: (image: UnsplashImage) => void;
  onPreview?: (image: UnsplashImage) => void;
  onSelect?: (image: UnsplashImage) => void;
  onDoubleSelect?: (image: UnsplashImage) => void;
  isSelected?: boolean;
  isSelecting?: boolean;
  isDownloading: boolean;
}

export function ImageCard({
  image,
  mode = 'menu',
  onAddToSiteFiles,
  onCrop,
  onPreview,
  onSelect,
  onDoubleSelect,
  isSelected = false,
  isSelecting = false,
  isDownloading,
}: ImageCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleMouseLeave = useCallback(() => {
    if (!isMenuOpen) {
      setIsHovered(false);
    }
  }, [isMenuOpen]);

  const handleAddToSiteFiles = useCallback(() => {
    onAddToSiteFiles?.(image);
  }, [onAddToSiteFiles, image]);

  const handleCrop = useCallback(() => {
    onCrop?.(image);
  }, [onCrop, image]);

  const handlePreview = useCallback(() => {
    onPreview?.(image);
  }, [onPreview, image]);

  if (mode === 'select') {
    return (
      <div
        className={cn(
          'group/unsplash-image-card relative mb-2 w-full break-inside-avoid overflow-hidden rounded-md border transition',
          isSelected
            ? 'border-primary ring-2 ring-primary/70'
            : 'border-border hover:border-primary/40',
        )}
      >
        <button
          type="button"
          onClick={() => {
            if (isSelecting) return;
            onSelect?.(image);
          }}
          onDoubleClick={() => {
            if (isSelecting) return;
            onDoubleSelect?.(image);
          }}
          aria-pressed={isSelected}
          disabled={isSelecting}
          aria-busy={isSelecting}
          className="block w-full text-left disabled:cursor-not-allowed disabled:opacity-90"
        >
          <img
            src={image.urls.small}
            alt={image.altDescription || 'Unsplash image'}
            className={cn('block h-auto w-full', isSelecting && isSelected && 'opacity-70')}
            loading="lazy"
          />
        </button>
        <PhotoCredit userName={image.user.name} userUrl={image.user.links.html} />
      </div>
    );
  }

  return (
    <div
      className="group/unsplash-image-card relative pb-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative border border-border overflow-hidden aspect-square">
        <img
          src={image.urls.small}
          alt={image.altDescription || 'Unsplash image'}
          className="h-full w-full object-cover"
          loading="lazy"
        />

        <LoadingOverlay show={isDownloading} />

        {isHovered && !isDownloading && (
          <div className="absolute inset-0 flex flex-col justify-between p-2 z-10">
            <ImageMenu
              onAddToSiteFiles={handleAddToSiteFiles}
              onCrop={onCrop ? handleCrop : undefined}
              onPreview={onPreview ? handlePreview : undefined}
              onOpenChange={setIsMenuOpen}
            />
            <PhotoCredit userName={image.user.name} userUrl={image.user.links.html} />
          </div>
        )}
      </div>
    </div>
  );
}
